/**
 * Gaze-reactive props for the admin eye-tracking lab's live view.
 *
 * Four toys with real-ish physics, each powered by being looked at:
 * - Fan: gaze applies torque, friction spins it down (angular velocity)
 * - Balloon: inflates under gaze, slowly deflates, pops at max and regrows
 * - Bulb: filament warms toward full glow with thermal lag, cools when ignored
 * - Rocket: gaze = thrust vs gravity; lifts off, falls back, bounces
 *
 * All animation runs in a single rAF loop mutating DOM refs directly — no
 * React re-renders. Gaze comes in via a ref written by the tracking loop.
 */
import { useEffect, useRef, type CSSProperties } from 'react'

export interface GazePoint {
  x: number
  y: number
  t: number
}

interface GazePropsLayerProps {
  gazeRef: { readonly current: GazePoint | null }
}

/** Normalised anchor positions (viewport fractions). */
const FAN_POS = { x: 0.18, y: 0.3 }
const BALLOON_POS = { x: 0.82, y: 0.32 }
const BULB_POS = { x: 0.18, y: 0.74 }
const ROCKET_POS = { x: 0.82, y: 0.78 }

/** Gaze hit radius (px) — generous, webcam gaze is ~100px accurate. */
const HIT_RADIUS = 130
/** Gaze samples older than this (face lost / blink) count as "not looking". */
const GAZE_FRESH_MS = 400

export default function GazePropsLayer({ gazeRef }: GazePropsLayerProps) {
  const fanBladesRef = useRef<SVGGElement>(null)
  const fanRingRef = useRef<HTMLDivElement>(null)
  const balloonBodyRef = useRef<SVGGElement>(null)
  const balloonBurstRef = useRef<SVGGElement>(null)
  const balloonRingRef = useRef<HTMLDivElement>(null)
  const bulbGlowRef = useRef<HTMLDivElement>(null)
  const bulbHotRef = useRef<SVGGElement>(null)
  const bulbRingRef = useRef<HTMLDivElement>(null)
  const rocketBodyRef = useRef<SVGGElement>(null)
  const rocketFlameRef = useRef<SVGGElement>(null)
  const rocketRingRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Mutable physics state, integrated per frame.
    const sim = {
      last: performance.now(),
      fanAngle: 0, // deg
      fanOmega: 0, // deg/s
      balloonSize: 0.5, // scale factor
      balloonBurstT: -1, // >=0 while burst animation plays
      bulbHeat: 0, // 0..1
      rocketY: 0, // px offset, negative = up
      rocketV: 0, // px/s
      t: 0,
    }

    const gazedAt = (cx: number, cy: number): boolean => {
      const g = gazeRef.current
      if (!g || performance.now() - g.t > GAZE_FRESH_MS) return false
      return Math.hypot(g.x - cx, g.y - cy) < HIT_RADIUS
    }

    const setRing = (el: HTMLDivElement | null, on: boolean) => {
      if (el) el.style.opacity = on ? '1' : '0'
    }

    let raf = 0
    const tick = () => {
      const now = performance.now()
      const dt = Math.min((now - sim.last) / 1000, 0.05)
      sim.last = now
      sim.t += dt
      const w = window.innerWidth
      const h = window.innerHeight

      // --- Fan: torque under gaze, linear drag → terminal speed ---
      const fanGazed = gazedAt(FAN_POS.x * w, FAN_POS.y * h)
      const torque = fanGazed ? 2600 : 0 // deg/s²
      const drag = 1.4 * sim.fanOmega
      sim.fanOmega = Math.max(0, sim.fanOmega + (torque - drag) * dt)
      sim.fanAngle = (sim.fanAngle + sim.fanOmega * dt) % 360
      if (fanBladesRef.current) {
        fanBladesRef.current.setAttribute('transform', `rotate(${sim.fanAngle})`)
      }
      setRing(fanRingRef.current, fanGazed)

      // --- Balloon: inflate under gaze, leak otherwise, pop at max ---
      const balloonGazed = gazedAt(BALLOON_POS.x * w, BALLOON_POS.y * h)
      if (sim.balloonBurstT >= 0) {
        sim.balloonBurstT += dt
        const p = sim.balloonBurstT / 0.5
        if (balloonBurstRef.current) {
          balloonBurstRef.current.style.opacity = String(Math.max(0, 1 - p))
          // scale about the balloon centre (0,-56)
          balloonBurstRef.current.setAttribute(
            'transform',
            `translate(0 -56) scale(${(0.6 + p).toFixed(3)}) translate(0 56)`,
          )
        }
        if (sim.balloonBurstT > 0.7) {
          sim.balloonBurstT = -1
          sim.balloonSize = 0.45
        }
      } else {
        sim.balloonSize += (balloonGazed ? 0.32 : -0.1) * dt
        sim.balloonSize = Math.max(0.45, sim.balloonSize)
        if (sim.balloonSize >= 1.35) {
          sim.balloonBurstT = 0
          if (balloonBurstRef.current) balloonBurstRef.current.style.opacity = '1'
        }
      }
      if (balloonBodyRef.current) {
        const popped = sim.balloonBurstT >= 0
        const bob = Math.sin(sim.t * 2.2) * 3 * sim.balloonSize
        balloonBodyRef.current.style.opacity = popped ? '0' : '1'
        // scale about the knot (0,-8) so the balloon stays tied to its string
        balloonBodyRef.current.setAttribute(
          'transform',
          `translate(0 ${bob.toFixed(1)}) translate(0 -8) scale(${sim.balloonSize.toFixed(3)}) translate(0 8)`,
        )
      }
      setRing(balloonRingRef.current, balloonGazed)

      // --- Bulb: first-order thermal lag toward gaze target ---
      const bulbGazed = gazedAt(BULB_POS.x * w, BULB_POS.y * h)
      const heatTarget = bulbGazed ? 1 : 0.05
      sim.bulbHeat += (heatTarget - sim.bulbHeat) * Math.min(dt / 0.55, 1)
      if (bulbGlowRef.current) bulbGlowRef.current.style.opacity = String(sim.bulbHeat)
      if (bulbHotRef.current) bulbHotRef.current.style.opacity = String(sim.bulbHeat)
      setRing(bulbRingRef.current, bulbGazed)

      // --- Rocket: thrust vs gravity, ground bounce, hover ceiling ---
      const rocketGazed = gazedAt(ROCKET_POS.x * w, ROCKET_POS.y * h + sim.rocketY)
      const accel = rocketGazed ? -620 : 440 // px/s², negative = up
      sim.rocketV += accel * dt
      sim.rocketY += sim.rocketV * dt
      const ceiling = -(ROCKET_POS.y - 0.28) * h // don't fly into the top HUD
      if (sim.rocketY > 0) {
        sim.rocketY = 0
        sim.rocketV = Math.abs(sim.rocketV) > 60 ? -sim.rocketV * 0.25 : 0
      } else if (sim.rocketY < ceiling) {
        sim.rocketY = ceiling
        sim.rocketV = 0
      }
      if (rocketBodyRef.current) {
        const wobble = rocketGazed ? Math.sin(sim.t * 40) * 1.2 : 0
        rocketBodyRef.current.setAttribute(
          'transform',
          `translate(${wobble.toFixed(1)} ${sim.rocketY.toFixed(1)})`,
        )
      }
      if (rocketFlameRef.current) {
        const flame = rocketGazed ? 0.85 + Math.sin(sim.t * 30) * 0.15 : 0
        rocketFlameRef.current.style.opacity = String(flame)
        // stretch downward from the nozzle line (y=30)
        rocketFlameRef.current.setAttribute(
          'transform',
          `translate(0 ${(sim.rocketY + 30).toFixed(1)}) scale(1 ${(0.7 + flame * 0.6).toFixed(2)}) translate(0 -30)`,
        )
      }
      setRing(rocketRingRef.current, rocketGazed)

      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [gazeRef])

  const anchor = (pos: { x: number; y: number }): CSSProperties => ({
    position: 'absolute',
    left: `${pos.x * 100}%`,
    top: `${pos.y * 100}%`,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  })

  const gazeRing: CSSProperties = {
    position: 'absolute',
    inset: -18,
    borderRadius: '50%',
    border: '2px solid rgba(255,122,92,0.65)',
    opacity: 0,
    transition: 'opacity 0.25s ease',
    pointerEvents: 'none',
  }

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* Fan */}
      <div style={anchor(FAN_POS)}>
        <div ref={fanRingRef} style={gazeRing} />
        <svg width="150" height="160" viewBox="-75 -75 150 160">
          {/* stand */}
          <rect x="-5" y="66" width="10" height="14" rx="3" fill="#5c6570" />
          <rect x="-24" y="78" width="48" height="7" rx="3.5" fill="#5c6570" />
          {/* guard */}
          <circle r="66" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
          <circle r="44" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
          <g ref={fanBladesRef}>
            {[0, 90, 180, 270].map((r) => (
              <path
                key={r}
                d="M0 0 C 12 -16, 16 -40, 3 -58 C -9 -44, -9 -18, 0 0 Z"
                fill="#9fb4d8"
                transform={`rotate(${r})`}
              />
            ))}
            <circle r="11" fill="#cfd4dc" />
            <circle r="4" fill="#5c6570" />
          </g>
        </svg>
      </div>

      {/* Balloon */}
      <div style={anchor(BALLOON_POS)}>
        <div ref={balloonRingRef} style={gazeRing} />
        <svg width="150" height="190" viewBox="-75 -120 150 190">
          {/* string (static, hangs from the knot area) */}
          <path d="M0 -8 C 8 12, -8 32, 2 58" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          <g ref={balloonBodyRef}>
            <ellipse cx="0" cy="-56" rx="36" ry="46" fill="#ff7a5c" />
            <ellipse cx="-13" cy="-72" rx="9" ry="14" fill="rgba(255,255,255,0.35)" />
            <path d="M-7 -11 L7 -11 L0 -2 Z" fill="#e05a3e" />
          </g>
          <g ref={balloonBurstRef} opacity="0">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((r) => (
              <line
                key={r}
                x1="0"
                y1="-56"
                x2={0 + 52 * Math.cos((r * Math.PI) / 180)}
                y2={-56 + 52 * Math.sin((r * Math.PI) / 180)}
                stroke="#ff7a5c"
                strokeWidth="4"
                strokeLinecap="round"
              />
            ))}
          </g>
        </svg>
      </div>

      {/* Bulb */}
      <div style={anchor(BULB_POS)}>
        <div ref={bulbRingRef} style={gazeRing} />
        <div
          ref={bulbGlowRef}
          style={{
            position: 'absolute',
            inset: -50,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,214,120,0.55), transparent 70%)',
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
        <svg width="110" height="150" viewBox="-40 -60 80 110" style={{ position: 'relative' }}>
          {/* glass */}
          <circle r="30" cy="-18" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
          {/* cold filament */}
          <path d="M-9 0 L-9 -18 L-4 -26 L0 -14 L4 -26 L9 -18 L9 0" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
          {/* hot overlay: warm glass + bright filament */}
          <g ref={bulbHotRef} style={{ opacity: 0 }}>
            <circle r="30" cy="-18" fill="rgba(255,214,120,0.45)" />
            <path d="M-9 0 L-9 -18 L-4 -26 L0 -14 L4 -26 L9 -18 L9 0" fill="none" stroke="#ffe9b0" strokeWidth="2.5" />
          </g>
          {/* base */}
          <rect x="-12" y="9" width="24" height="16" rx="3" fill="#8a919c" />
          <line x1="-12" y1="14" x2="12" y2="14" stroke="#5c6570" strokeWidth="2" />
          <line x1="-12" y1="19" x2="12" y2="19" stroke="#5c6570" strokeWidth="2" />
          <path d="M-7 25 L7 25 L3 31 L-3 31 Z" fill="#5c6570" />
        </svg>
      </div>

      {/* Rocket */}
      <div style={anchor(ROCKET_POS)}>
        <div ref={rocketRingRef} style={gazeRing} />
        <svg width="130" height="260" viewBox="-65 -200 130 260" style={{ overflow: 'visible' }}>
          {/* launch pad */}
          <line x1="-34" y1="42" x2="34" y2="42" stroke="rgba(255,255,255,0.35)" strokeWidth="3" strokeLinecap="round" />
          <g ref={rocketFlameRef} style={{ opacity: 0 }}>
            <path d="M-9 30 C -6 46, 6 46, 9 30 C 4 36, -4 36, -9 30 Z" fill="#ffb64d" />
            <path d="M-5 30 C -3 42, 3 42, 5 30 Z" fill="#ffe9b0" />
          </g>
          <g ref={rocketBodyRef}>
            {/* fins */}
            <path d="M-14 12 L-30 34 L-14 30 Z" fill="#e05a3e" />
            <path d="M14 12 L30 34 L14 30 Z" fill="#e05a3e" />
            {/* body */}
            <path d="M-14 30 L-14 -18 C -14 -44, 14 -44, 14 -18 L14 30 Z" fill="#dfe5ee" />
            {/* nose */}
            <path d="M-14 -18 C -14 -44, 14 -44, 14 -18 C 8 -34, -8 -34, -14 -18 Z" fill="#ff7a5c" />
            <circle cy="-6" r="7" fill="#3d556e" stroke="rgba(255,255,255,0.6)" strokeWidth="2" />
            <rect x="-14" y="22" width="28" height="8" fill="#b8c2d0" />
          </g>
        </svg>
      </div>
    </div>
  )
}
